import { Injectable } from '@angular/core';
import {
	Application,
	ApplicationLearningType,
	ExpenseType,
	MicroApplication,
	User
} from '@edassist/api';
import { PROGRAM_TYPES } from '../../enums/program-types.enum';
import { UI_CONSTANTS } from '@edassist/shared';
import { STATUS_HISTORY } from '../../enums/status-history.enum';
import { Permissions } from '../../models/permissions';
import { AppStatusConfig } from '../../models/application-status-config.interface';
import { ApplicationStatusUtilityService } from '../application-status/application-status-utility.service';
import {
	APPLICATION_STATUSES,
	constants,
	USER_TYPES,
	UserTypeService
} from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class ApplicationUtilityService {
	static isExamType(applicationLearningType: ApplicationLearningType): boolean {
		return (
			applicationLearningType &&
			(applicationLearningType.certificationType.id ===
				UI_CONSTANTS.CERTIFICATION_TYPES.EXAM ||
				applicationLearningType.certificationType.id ===
					UI_CONSTANTS.CERTIFICATION_TYPES.PREP_COURSE)
		);
	}

	static isEdGoalApplication(programTypeId: number): boolean {
		return programTypeId === PROGRAM_TYPES.edGoal;
	}

	static isLoanApplcation(programTypeId: number): boolean {
		return [
			PROGRAM_TYPES.loanRepayDirectPay,
			PROGRAM_TYPES.loanReimbursement
		].includes(programTypeId);
	}

	static isLoanApplicationType(applicationTypeId: number): boolean {
		return [
			constants.APPLICATION_TYPES.loanDirectPay,
			constants.APPLICATION_TYPES.loanReimbursement
		].includes(applicationTypeId);
	}

	static getCourseRelatedExpenses(
		expenses: Array<ExpenseType>
	): Array<ExpenseType> {
		return expenses.filter((expense) => expense.courseRelated);
	}

	static getNonCourseRelatedExpenses(
		expenses: Array<ExpenseType>
	): Array<ExpenseType> {
		return expenses.filter((expense) => !expense.courseRelated);
	}

	static combineRelatedApplications(
		...relatedApps: Array<any>
	): Array<MicroApplication> | Array<Application> {
		return [...relatedApps].reduce((related, currentValue) => {
			if (currentValue) {
				Array.isArray(currentValue) ?
					related.push(...currentValue)
				:	related.push(currentValue);
			}

			return related;
		}, []);
	}

	static isAllowedToResolveRule(
		status: string,
		permissions: Permissions
	): boolean {
		return (
			(status === STATUS_HISTORY.MANUAL && permissions.RESOLVE_MANUAL_RULES) ||
			(status === STATUS_HISTORY.INCOMPLETE &&
				permissions.RESOLVE_INCOMPLETE_RULES) ||
			(status === STATUS_HISTORY.DENY && permissions.RESOLVE_DENY_RULES) ||
			(status === STATUS_HISTORY.CARRYOVER &&
				permissions.RESOLVE_CARRYOVER_RULES) ||
			(status === STATUS_HISTORY.CARRYOVER_SPLIT &&
				permissions.RESOLVE_CARRYOVER_RULES)
		);
	}

	static disableCreateNewApplication(user: User): boolean {
		return (
			!user?.hasEligiblePrograms ||
			(user?.hasEdGoalPrograms && !user?.hasApprovedEdGoals)
		);
	}

	static getAppStatusConfig(applicationStatusCode: number): AppStatusConfig {
		if (applicationStatusCode > 10) {
			return ApplicationStatusUtilityService.getApplicationStatus(
				applicationStatusCode
			);
		}

		return ApplicationStatusUtilityService.getAppealStatus(
			applicationStatusCode
		);
	}

	static isReimbursement(application: Application): boolean {
		return (
			application?.benefitPeriod.program.programType?.id ===
			PROGRAM_TYPES.reimbursement
		);
	}

	static isDirectBill(application: Application): boolean {
		return (
			application?.benefitPeriod.program.programType?.id ===
			PROGRAM_TYPES.directBill
		);
	}

	static isEligibleToAddCourse(application: Application): boolean {
		// maxNumOfCourses is null then no limit for adding course, coursesCount is null then no courses added yet
		const isValidCourseCount =
			!application.benefitPeriod?.program?.maxNumOfCourses ||
			!application.coursesCount ||
			(application.benefitPeriod?.program?.maxNumOfCourses &&
				application.coursesCount &&
				application.coursesCount <
					application.benefitPeriod?.program?.maxNumOfCourses);

		return (
			isValidCourseCount &&
			((ApplicationUtilityService.isReimbursement(application) &&
				application.applicationStatus.id === APPLICATION_STATUSES.approved) ||
				(ApplicationUtilityService.isDirectBill(application) &&
					application.applicationStatus.id ===
						APPLICATION_STATUSES.locIssued) ||
				application.applicationStatus.id ===
					APPLICATION_STATUSES.paymentProcessingIncomplete ||
				application.applicationStatus.id ===
					APPLICATION_STATUSES.paymentCompleteCompletionDocumentsRequired)
		);
	}

	static isExpensesConfigured(application: Application): boolean {
		// Expenses configuration is on program setup, step 2, Related Programs.
		return !!application.benefitPeriod.program.reimburseBookProgramId;
	}

	static isLocIssued(application: Application): boolean {
		return application.applicationStatus.id === APPLICATION_STATUSES.locIssued;
	}
}
